import React from 'react';
import { Row, Col, Card, Spin, Tag } from 'antd';
import {
    DollarCircleOutlined,
    CalendarOutlined as DateIcon,
    TagsOutlined,
    UserOutlined
} from '@ant-design/icons';
import { FlexBox, TextCommon } from '@esg/ui';
import { useHttpQuery } from '@esg/framework';
import { agencyApis } from '@esg/business-agency';
import { dayjs } from '@esg/shared';

interface BranchSubscriptionCardProps {
    readonly branchId: string;
    readonly startTime: string;
    readonly endTime: string;
}

const cardStyle: React.CSSProperties = {
    borderRadius: '12px',
    border: '1px solid #eef2f5',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
    height: '100%'
};

export const BranchSubscriptionCard: React.FC<BranchSubscriptionCardProps> = ({ branchId, startTime, endTime }) => {
    const { data: subResponse, isFetching: isSubFetching } = useHttpQuery(
        agencyApis.subscription.getAllBranchSubscription,
        { query: { page: 1, branchId, startTime, endTime } },
        { enabled: !!branchId }
    );

    const { data: employeeOverview, isFetching: isEmpFetching } = useHttpQuery(
        agencyApis.employee.getEmployeeOverview,
        { query: { page: 1, branchId, startTime, endTime } },
        { enabled: !!branchId }
    );

    const isFetching = isSubFetching || isEmpFetching;

    const latestSub = subResponse?.items?.[0];
    const planName = latestSub?.name ?? 'No Active Plan';
    const planPrice = latestSub?.price ? `$${latestSub.price}` : '$0';
    const isActive = latestSub?.planSubscription?.isActive === true;
    const effectiveDate = latestSub?.effectiveTime ? dayjs(latestSub.effectiveTime).format('MM/DD/YYYY') : '-';
    const expirationDate = latestSub?.expiredTime ? dayjs(latestSub.expiredTime).format('MM/DD/YYYY') : '-';
    const totalEmployee = employeeOverview?.totalEmployee ?? 0;

    return (
        <Card
            title={
                <TextCommon fontWeight={600} fontSize={15} color="#1f2937">
                    Branch Plan & Subscription Details
                </TextCommon>
            }
            style={cardStyle}
            bodyStyle={{ padding: '24px' }}
        >
            <Spin spinning={isFetching}>
                <Row gutter={[24, 24]} align="top">
                    
                    {/* Total Employees */}
                    <Col xs={24} sm={12} style={{ flex: '1 0 0%' }}>
                        <FlexBox direction="column" gap={6}>
                            <FlexBox gap={6}>
                                <UserOutlined style={{ color: '#722ed1', fontSize: 13 }} />
                                <TextCommon color="#8c8c8c" fontSize={11} fontWeight={600} letterSpacing="0.4px">
                                    TOTAL EMPLOYEES
                                </TextCommon>
                            </FlexBox>
                            <TextCommon fontSize={20} fontWeight={700} color="#1f2937">
                                {totalEmployee}
                            </TextCommon>
                            <TextCommon color="#8c8c8c" fontSize={11}>
                                Active branch staff
                            </TextCommon>
                        </FlexBox>
                    </Col>

                    {/* Active Plan */}
                    <Col xs={24} sm={12} style={{ flex: '1 0 0%' }}>
                        <FlexBox direction="column" gap={6}>
                            <FlexBox gap={6}>
                                <TagsOutlined style={{ color: '#1890ff', fontSize: 13 }} />
                                <TextCommon color="#8c8c8c" fontSize={11} fontWeight={600} letterSpacing="0.4px">
                                    ACTIVE PLAN
                                </TextCommon>
                            </FlexBox>
                            <TextCommon fontSize={18} fontWeight={700} color="#1f2937">
                                {planName}
                            </TextCommon>
                            <FlexBox >
                                <Tag color={isActive && latestSub ? 'success' : 'error'} style={{ borderRadius: '12px', padding: '1px 10px', margin: 0, fontSize: 11 }}>
                                    {isActive && latestSub ? '● Active' : '● Expired'}
                                </Tag>
                            </FlexBox>
                        </FlexBox>
                    </Col>

                    {/* Plan Price */}
                    <Col xs={24} sm={12} style={{ flex: '1 0 0%' }}>
                        <FlexBox direction="column" gap={6}>
                            <FlexBox gap={6}>
                                <DollarCircleOutlined style={{ color: '#52c41a', fontSize: 13 }} />
                                <TextCommon color="#389e0d" fontSize={11} fontWeight={600} letterSpacing="0.4px">
                                    PLAN PRICE
                                </TextCommon>
                            </FlexBox>
                            <TextCommon fontSize={22} fontWeight={700} color="#237804">
                                {planPrice}
                            </TextCommon>
                            <TextCommon color="#73d13d" fontSize={11}>
                                Billed per cycle
                            </TextCommon>
                        </FlexBox>
                    </Col>

                    {/* Effective Date */}
                    <Col xs={24} sm={12} style={{ flex: '1 0 0%' }}>
                        <FlexBox direction="column" gap={6}>
                            <FlexBox gap={6}>
                                <DateIcon style={{ color: '#8c8c8c', fontSize: 13 }} />
                                <TextCommon color="#8c8c8c" fontSize={11} fontWeight={600} letterSpacing="0.4px">
                                    EFFECTIVE DATE
                                </TextCommon>
                            </FlexBox>
                            <TextCommon fontSize={16} fontWeight={600} color="#262626">
                                {effectiveDate}
                            </TextCommon>
                            <TextCommon color="#bfbfbf" fontSize={11}>
                                Plan start date
                            </TextCommon>
                        </FlexBox>
                    </Col>

                    {/* Expiration Date */}
                    <Col xs={24} sm={12} style={{ flex: '1 0 0%' }}>
                        <FlexBox direction="column" gap={6}>
                            <FlexBox gap={6}>
                                <DateIcon style={{ color: '#8c8c8c', fontSize: 13 }} />
                                <TextCommon color="#8c8c8c" fontSize={11} fontWeight={600} letterSpacing="0.4px">
                                    EXPIRATION DATE
                                </TextCommon>
                            </FlexBox>
                            <TextCommon fontSize={16} fontWeight={600} color="#262626">
                                {expirationDate}
                            </TextCommon>
                            <TextCommon color="#bfbfbf" fontSize={11}>
                                Renewal or expiry date
                            </TextCommon>
                        </FlexBox>
                    </Col>

                </Row>
            </Spin>
        </Card>
    );
};